import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import { DataGrid, ptBR } from "@mui/x-data-grid";
import { cancel, getAdvanceRequests, updateDatePayment } from "~/services/antecipebackoffice/advanceRequest";
import { getDutysByIdDoctor } from "~/services/antecipebackoffice/dutys";
import CustomPagination from "~/components/CustomPagination/index";
import DutysModal from "~/components/modals/DutysModalAntecipe";
import ChangeStatusModal from "~/components/modals/ChangeStatusModal";
import GenerateContractModal from "~/components/modals/GenerateContractModal";
import { packageContext } from "~/components/modals/packageContext";
import FilterBarAdvanceRequest from "~/components/FilterBarAdvanceRequest";
import { useLatestRequest } from "~/shared/ui/filters/useLatestRequest";
import ReadOnlyChip from "~/components/ReadOnlyChip";
import { useAuth } from "~/auth/AuthProvider";
import { advanceRequestToSearch, DEFAULT_ADVANCE_REQUEST_FILTERS, parseAdvanceRequestSearch } from "~/utils/advanceRequest";
import { getApiErrorMessage } from "~/utils/apiError";
import ConfirmDialog from "~/shared/ui/ConfirmDialog";
import EmptyState from "~/shared/ui/EmptyState";
import ErrorState from "~/shared/ui/ErrorState";
import InactiveChip from "~/shared/ui/InactiveChip";
import PageHeader from "~/shared/ui/PageHeader";
import StatusChip from "~/shared/ui/StatusChip";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { formatBRL, formatDateBR } from "~/shared/ui/format";
import { usePreferences } from "~/features/preferences/PreferencesProvider";
import ColumnPicker from "~/features/antecipacoes/ColumnPicker";
import PaymentDateCell from "~/features/antecipacoes/PaymentDateCell";
import RowActionsMenu from "~/features/antecipacoes/RowActionsMenu";
import { buildColumnVisibilityModel } from "~/features/antecipacoes/columns";
import { CancelPackageDialog, isReasonRequired } from "~/features/feedback";
import { useFeatures } from "~/hooks/useFeatures";
import { ReopenDialog, SlaBadge, StatusHistoryDrawer } from "~/features/packages";
import { brtShort, ChannelChip, deadlineLabel, ExpiredMotiveChip, PackageSealChip, SlaPausedChip } from "~/features/registration-seal";

const LIMIT = 50;
const LIMIT_DUTYS = 10;

export const createFilterObject = (filters) => {
  const out = {};
  if (filters.doctorName) out.fullName = filters.doctorName;
  if (filters.crm) out.crm = filters.crm;
  if (filters.crmUf) out.crmUf = filters.crmUf;
  if (filters.selectedStatus && filters.selectedStatus.length > 0) out.status = filters.selectedStatus;
  if (filters.startRequestDate) out.startRequestDate = filters.startRequestDate;
  if (filters.endRequestDate) out.endRequestDate = filters.endRequestDate;
  if (filters.includeInactive) out.includeInactive = true;
  // Conversão do ME (BE-C2): pior selo do pacote ∈ PENDING | IN_AUDIT.
  if (filters.registrationPending) out.registration = "pending";
  return out;
};

const money = (field, headerName) => ({ field, headerName, width: 140, type: "number", sortable: false, valueFormatter: ({ value }) => formatBRL(value) });

export default function TableAntecipe() {
  const { permissions } = useAuth();
  const canWrite = permissions.backofficeWrite;
  const { prefs, setPref } = usePreferences();
  const notify = useNotify();
  const { features } = useFeatures();
  // Conversão do ME (FE-C2): chaves desligadas = grade idêntica à de hoje, mesmo que o BE mande os campos.
  const seal = features.registrationSeal === true;
  const pause = features.slaRegistrationPause === true;
  const sealRef = useRef(seal);
  sealRef.current = seal;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialUrlState] = useState(() => parseAdvanceRequestSearch(searchParams));
  const urlStateRef = useRef(initialUrlState);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialUrlState.page);
  const [order, setOrder] = useState(initialUrlState.order);
  const [filters, setFilters] = useState(() => createFilterObject(initialUrlState.filters));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filterBarKey, setFilterBarKey] = useState(0);
  const [statusRow, setStatusRow] = useState(null);
  const [contractRow, setContractRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  // Janela (a) com motivo: pela flag (proativo) ou pelo 422 REASON_REQUIRED do cancelamento antigo (reativo).
  const [reasonCancelRow, setReasonCancelRow] = useState(null);
  // F1b: reabrir expirado (só com a chave packageExpiration).
  const [reopenRow, setReopenRow] = useState(null);
  const [dutysRow, setDutysRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [dutys, setDutys] = useState({ open: false, rows: [], total: 0 });

  const syncUrl = (patch) => {
    urlStateRef.current = { ...urlStateRef.current, ...patch };
    setSearchParams(advanceRequestToSearch(urlStateRef.current), { replace: true });
  };

  const beginRequest = useLatestRequest();
  const getData = async (pageSelected = page, f = filters, o = order) => {
    // Última requisição vence: resposta de um filtro antigo não sobrescreve a do atual (decisão U1).
    const isLatest = beginRequest();
    setLoading(true);
    setLoadError(null);
    try {
      const params = { ...f, orderByRequestDate: o };
      // registro=pendente da URL só vale com a chave registrationSeal.
      if (!sealRef.current) delete params.registration;
      const response = await getAdvanceRequests({ limit: LIMIT, offset: (pageSelected - 1) * LIMIT }, params);
      if (!isLatest()) return;
      const totalPages = Math.ceil(response.data.total_items / LIMIT);
      if (pageSelected > totalPages && totalPages !== 0) {
        setPage(totalPages);
        syncUrl({ page: totalPages });
        const again = await getAdvanceRequests({ limit: LIMIT, offset: (totalPages - 1) * LIMIT }, params);
        if (!isLatest()) return;
        setTotal(again.data.total_items);
        setRows(again.data.data);
      } else {
        setTotal(response.data.total_items);
        setRows(response.data.data);
      }
    } catch (e) {
      if (isLatest()) setLoadError(getApiErrorMessage(e, "Não foi possível carregar as antecipações."));
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A chave chega depois do /features: refaz a busca se a URL já pedia "Só com registro pendente".
  const prevSeal = useRef(seal);
  useEffect(() => {
    if (seal && !prevSeal.current && urlStateRef.current.filters.registrationPending) getData();
    prevSeal.current = seal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seal]);
  const barFilters = seal ? urlStateRef.current.filters : { ...urlStateRef.current.filters, registrationPending: false };

  const applyFilters = (params) => {
    const f = createFilterObject(params);
    setPage(1);
    setFilters(f);
    syncUrl({ filters: params, page: 1 });
    getData(1, f, order);
  };
  const clearFilters = () => {
    setFilterBarKey((k) => k + 1);
    urlStateRef.current = { ...urlStateRef.current, filters: { ...DEFAULT_ADVANCE_REQUEST_FILTERS } };
    applyFilters({ ...DEFAULT_ADVANCE_REQUEST_FILTERS });
  };

  const includeInactive = () => {
    // Remonta a barra para o switch refletir o filtro aplicado pelo vazio.
    setFilterBarKey((k) => k + 1);
    const next = { ...urlStateRef.current.filters, includeInactive: true };
    urlStateRef.current = { ...urlStateRef.current, filters: next };
    applyFilters(next);
  };

  const saveDate = async (id, date) => {
    try {
      await updateDatePayment(id, date);
      notify({ severity: "success", message: "Data de pagamento atualizada com sucesso!" });
      await getData();
    } catch (e) {
      notify({ severity: "error", message: getApiErrorMessage(e, "Erro ao atualizar data!") });
    }
  };

  const loadDutys = async (id, params) => {
    const result = await getDutysByIdDoctor(id, params);
    setDutys((d) => ({ ...d, open: true, rows: result?.data || [], total: result?.total_items || 0 }));
  };
  const openDutys = (row) => {
    setDutysRow(row);
    setDutys({ open: true, rows: [], total: 0 });
    loadDutys(row.id, { limit: LIMIT_DUTYS, offset: 0 });
  };

  const openCancel = (row) => (features.feedbackCapture ? setReasonCancelRow(row) : setCancelRow(row));

  const confirmCancel = async () => {
    try {
      await cancel(cancelRow.id);
    } catch (e) {
      // Flag do FE desatualizada e BE já exigindo motivo: troca o confirm antigo pela janela (a).
      if (isReasonRequired(e)) {
        setReasonCancelRow(cancelRow);
        return;
      }
      throw e;
    }
    notify({ severity: "success", message: "Solicitação cancelada com sucesso!" });
    await getData();
  };

  const slaCell = ({ row }) => {
    if (pause && row.slaPause) {
      // Mockup V1 (slaCell 'sis'): prazo do registro abaixo do chip; sem prazo no pacote, desde quando está pausado.
      const detail = deadlineLabel({ ...row.registration, status: row.registration?.worst === "IN_AUDIT" ? "IN_AUDIT" : "PENDING" }) || (brtShort(row.slaPause.since) ? `desde ${brtShort(row.slaPause.since)}` : null);
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25 }}>
          <SlaPausedChip pause={row.slaPause} />
          {detail && <Box component="span" data-testid="sla-paused-detail" sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>{detail}</Box>}
        </Box>
      );
    }
    if (pause && row.status === "expired" && row.motive) return <ExpiredMotiveChip motive={row.motive} compact />;
    return <SlaBadge sla={row.sla} registrationPause={pause} />;
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90, sortable: false, renderCell: ({ value }) => <b>#{value}</b> },
    { field: "doctor_name", headerName: "Nome", flex: 1, minWidth: 200, sortable: false },
    ...(seal
      ? [
          { field: "channel", headerName: "Canal", width: 130, sortable: false, renderCell: ({ row }) => <ChannelChip channel={row.channel} /> },
          { field: "registration", headerName: "Registro no ME", width: 190, sortable: false, renderCell: ({ row }) => <PackageSealChip registration={row.registration} /> },
        ]
      : []),
    { field: "crm", headerName: "CRM", width: 120, sortable: false, valueGetter: ({ row }) => [row.doctor_crm, row.doctor_crm_uf].filter(Boolean).join("/") },
    {
      field: "company_name", headerName: "Empresa", width: 240, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Box component="span" title={row.company_name} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.company_name}</Box>
          {/* ausente (BE antigo) = ativa */}
          {row.company_active === false && <InactiveChip />}
        </Box>
      ),
    },
    { field: "request_date", headerName: "Data da solicitação", width: 160, sortable: true, valueFormatter: ({ value }) => formatDateBR(value, "—") },
    { field: "payment_date", headerName: "Data de Pagamento", width: 220, sortable: false, renderCell: ({ row }) => <PaymentDateCell row={row} canWrite={canWrite} onSave={saveDate} /> },
    { field: "vacancies_qty", headerName: "Qtde. Plantões", width: 120, type: "number", sortable: false },
    money("total_gross_value", "Valor Bruto"),
    money("total_pay_value", "Valor a ser pago"),
    money("total_fee_value", "Valor total da taxa"),
    { field: "status", headerName: "Status", width: 190, sortable: false, renderCell: ({ value }) => <StatusChip status={value} /> },
    { field: "sla", headerName: "SLA", width: pause ? 210 : 130, sortable: false, renderCell: slaCell },
    {
      field: "actions", headerName: "Ações", width: 76, sortable: false, align: "center", headerAlign: "center",
      renderCell: ({ row }) => (
        <RowActionsMenu row={row} canWrite={canWrite} onChangeStatus={setStatusRow} onShowDutys={openDutys} onShowHistory={setHistoryRow} onGenerateContract={setContractRow} onCancel={openCancel} onReopen={features.packageExpiration ? setReopenRow : undefined} />
      ),
    },
  ];

  const empty = !loading && !loadError && rows.length === 0;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.5, p: { xs: 1.5, md: 3 }, minHeight: 0 }}>
      <PageHeader
        title="Antecipações"
        description="Pedidos de antecipação de todos os médicos e empresas. Valores em R$; datas dd/mm/aaaa."
        actions={
          <>
            {!canWrite && <ReadOnlyChip />}
            <ColumnPicker visible={prefs.antecipacoesColumns} onChange={(next) => setPref("antecipacoesColumns", next)} />
          </>
        }
      />
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <FilterBarAdvanceRequest key={`${filterBarKey}-${seal}`} onFilterChange={applyFilters} initialFilters={barFilters} showRegistrationFilter={seal} />
      </Paper>
      <Paper data-testid="antecipacoes-grid-frame" variant="outlined" sx={{ flexGrow: 1, minHeight: 420, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {loadError ? (
          <ErrorState description={loadError} onRetry={() => getData()} />
        ) : empty ? (
          <EmptyState
            title="Nenhuma antecipação encontrada com esses filtros"
            description="Revise o período (Data Inicial/Final) ou o status. Pedidos de empresas desativadas só aparecem com “Incluir empresas inativas”."
            actions={
              <>
                {!filters.includeInactive && <Button onClick={includeInactive}>Incluir empresas inativas</Button>}
                <Button variant="contained" onClick={clearFilters}>Limpar filtros</Button>
              </>
            }
          />
        ) : (
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            columnVisibilityModel={buildColumnVisibilityModel(prefs.antecipacoesColumns)}
            loading={loading}
            density="standard"
            // 44px: comporta os alvos de 40px das ações (spec a11y) sem invadir a linha vizinha
            rowHeight={44}
            headerHeight={40}
            disableVirtualization
            disableColumnMenu
            disableColumnFilter
            disableSelectionOnClick
            sortingMode="server"
            sortModel={[{ field: "request_date", sort: order }]}
            onSortModelChange={(model) => {
              const next = model[0]?.sort || (order === "desc" ? "asc" : "desc");
              setOrder(next);
              setPage(1);
              syncUrl({ page: 1, order: next });
              getData(1, filters, next);
            }}
            components={{
              Pagination: () => (
                <CustomPagination
                  count={Math.ceil(total / LIMIT)}
                  page={page}
                  setPage={setPage}
                  onChange={(event, value) => {
                    syncUrl({ page: value });
                    getData(value);
                  }}
                />
              ),
            }}
            sx={{ border: 0, flex: 1 }}
          />
        )}
      </Paper>
      {contractRow && <GenerateContractModal open setOpen={() => setContractRow(null)} advancePaymentSelected={contractRow} />}
      {statusRow && <ChangeStatusModal open setOpen={() => setStatusRow(null)} advancePayment={statusRow} updateData={() => getData()} />}
      <DutysModal
        open={dutys.open}
        setOpen={(open) => setDutys((d) => ({ ...d, open }))}
        dutys={dutys.rows}
        totalRegisters={dutys.total}
        advancePayment={dutysRow}
        updateListDutys={(id, params) => loadDutys(id || dutysRow.id, params)}
        updateRequestList={() => getData()}
        showRegistration={seal}
        canWrite={canWrite}
      />
      {reasonCancelRow && (
        <CancelPackageDialog
          row={reasonCancelRow}
          context={packageContext(reasonCancelRow)}
          onClose={() => setReasonCancelRow(null)}
          onCanceled={() => {
            setReasonCancelRow(null);
            getData();
          }}
        />
      )}
      {historyRow && <StatusHistoryDrawer open row={historyRow} onClose={() => setHistoryRow(null)} />}
      {reopenRow && (
        <ReopenDialog
          open
          row={reopenRow}
          context={packageContext(reopenRow)}
          onClose={() => setReopenRow(null)}
          onReopened={() => {
            setReopenRow(null);
            getData();
          }}
          // Antecipações não filtra por pacote: o vivo abre na tela Pacotes (?id=), que existe com mergeManual (lacuna 10).
          onOpenPackage={features.mergeManual ? (id) => navigate(`/admin/antecipe/pacotes?id=${id}`) : undefined}
        />
      )}
      <ConfirmDialog
        open={Boolean(cancelRow)}
        onClose={() => setCancelRow(null)}
        destructive
        title="Cancelar antecipação"
        context={cancelRow ? packageContext(cancelRow) : undefined}
        message="Esta ação não pode ser desfeita. O pedido sai da fila de antecipação; para antecipar estes plantões, o médico precisará fazer um novo pedido."
        confirmLabel="Cancelar a antecipação"
        cancelLabel="Voltar"
        onConfirm={confirmCancel}
        errorMessage={(e) => getApiErrorMessage(e, "Erro ao cancelar solicitação")}
      />
    </Box>
  );
}
